import axios from 'axios';

// On-call service requests on the route builder and Storm Mode (migration 102,
// server/services/serviceRequests.js). GET /api/routes/:id/service-requests
// gives the route's service date (the day it was created, in the company's zone) and, per
// ROOT customer, the open request for that day, the day after or the day
// before (same day wins, then the day after). A property
// follows its root customer, for the request and for on-call or seasonal.
//
//   on-call with a request   shown as usual, badge "Requested for Jan 14",
//                            sorted to the top of the list
//   on-call, no request      greyed, "On-call, no request", left out of Add
//                            all; adding one asks first, and the server notes
//                            the stop "Added without request"
//   seasonal                 unchanged

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const NO_REQUEST_LABEL = 'On-call, no request';
export const NO_REQUEST_CONFIRM = 'This on-call customer has not requested service. Add anyway?';

// The route's matching context, or null when it cannot be read: then nothing
// is greyed or badged, which is how the lists looked before requests existed.
export async function loadRouteRequests(routeId) {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/routes/${routeId}/service-requests`);
    return res.data;
  } catch (err) {
    console.error('Failed to load service requests:', err.response?.status || err.message);
    return null;
  }
}

// 'Requested for Jan 14' from '2027-01-14'.
export function requestBadgeText(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ''));
  if (!m) return 'Requested';
  return `Requested for ${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}`;
}

// The on-call or seasonal badge on the route builder's stops list and Storm
// Mode's stop cards. It reads the root customer's service_preference (NULL is
// on-call), the field the customer card and the request matching read. It
// used to read the legacy service_frequency, which every insert path writes
// as 'seasonal' (or left NULL), and anything but as_needed was badged
// Seasonal, so on-call customers were.
export function preferenceBadge(servicePreference) {
  return (servicePreference || 'on_call') === 'on_call'
    ? { label: 'On-call', className: 'as-needed' }
    : { label: 'Seasonal', className: 'seasonal' };
}

// The flags for one option; servicePreference is the root customer's, NULL
// reads as on-call. Mirrors the server's stopRequestFlags.
export function requestFlags(ctx, { servicePreference, rootId }) {
  const onCall = (servicePreference || 'on_call') === 'on_call';
  if (!ctx || !ctx.applies || !onCall) return { onCall, requestedDate: null, noRequest: false };
  const m = (ctx.requested || {})[rootId] || null;
  return { onCall, requestedDate: m ? m.requestedDate : null, noRequest: !m };
}

// Options with their flags, on-call customers who asked first (by requested
// date), then everyone else in the order given, then on-call customers who
// did not ask. Each option needs servicePreference and rootId.
export function withRequestFlags(ctx, options) {
  const flagged = (options || []).map((o) => ({ ...o, request: requestFlags(ctx, o) }));
  const asked = flagged.filter((o) => o.request.requestedDate)
    .sort((a, b) => (a.request.requestedDate < b.request.requestedDate ? -1
      : a.request.requestedDate > b.request.requestedDate ? 1 : 0));
  const rest = flagged.filter((o) => !o.request.requestedDate && !o.request.noRequest);
  const notAsked = flagged.filter((o) => o.request.noRequest);
  return [...asked, ...rest, ...notAsked];
}

// Ask before adding an on-call customer who did not request service. True to go ahead.
export function confirmNoRequest(flags) {
  if (!flags || !flags.noRequest) return true;
  return window.confirm(NO_REQUEST_CONFIRM);
}
